import React, { useContext, useState } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Button } from '@THC/components/Button/Button';

import { formatPrice } from 'Utilities/formatPrice';
import { formatPhoneNumber } from 'Utilities/helpers';

import { Modal } from 'Storybook/components/local/Modal/Modal';

import { PHONES } from '../../../Payments.helpers';
import LocalTexts from '../../../Payments.text.json';
import { PaymentContext } from '../../../PaymentsContext';

import modalBoxStyles from '../modalBoxStyles.scss';
import styles from './FinanceApplicable.scss';

const LocalT = new MDText(LocalTexts.payments.figuresBox.financeApplicable);

interface FinanceApplicableProps {
  financingAmountText?: string;
  sellerAmountText?: string;
  showIncorrectFinanceButton?: boolean;
  title?: string;
}

const FinanceApplicable = ({
  financingAmountText,
  sellerAmountText,
  showIncorrectFinanceButton,
  title,
}: FinanceApplicableProps) => {
  const [open, setOpen] = useState(false);

  const {
    paymentState: { accountNumber, financingAmount, hasNegativeEquity, reference, sellerAmount, vehicleAmount },
  } = useContext(PaymentContext);

  const handleOnClick = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  return (
    <div className={styles.financeApplicable}>
      <p>{title || LocalT.translate('title')}</p>
      <ul>
        {reference && (
          <li>
            <span className={styles.figure}>{reference}</span>
            {LocalT.translate('sellerReference')}
          </li>
        )}
        {hasNegativeEquity ? (
          <li>
            <span className={styles.figure}>{formatPrice(financingAmount)}</span>
            {LocalT.translate('amountPaidToFinanceCompany')}
            <ul className={styles.splitAmount}>
              <li>{LocalT.translate('sellerAmountPaid')}
                <span>{formatPrice(Math.abs(sellerAmount))}</span>
              </li>
              <li>{LocalT.translate('dealerAmountPaid')}
                <span>{formatPrice(vehicleAmount)}</span>
              </li>
            </ul>
          </li>
        ) : (
          <>
            <li>
              <span className={styles.figure}>{formatPrice(sellerAmount)}</span>
              {sellerAmountText || LocalT.translate('sellerAmount', { accountNumber })}
            </li>
            <li>
              <span className={styles.figure}>{formatPrice(financingAmount)}</span>
              {financingAmountText || LocalT.translate('financingAmount')}
              {showIncorrectFinanceButton && (
                <button
                  className={cx('mw-link', 'mw-link-blue', 'mw-link-small', styles.incorrectFigures)}
                  onClick={() => handleOnClick()}
                  role="button"
                >
                  {LocalT.translate('incorrectFinanceLabel')}
                </button>
              )}
            </li>
          </>
        )}
      </ul>

      {open && (
        <Modal className={modalBoxStyles.modal} dismissModal={() => setOpen(false)} size="small">
          <div className={modalBoxStyles.modalWrapper}>
            <div className={modalBoxStyles.stickyWrapper}>
              <h2>{LocalT.translate('modal.title')}</h2>
              <div className={modalBoxStyles.modalText}>
                {LocalT.translate('modal.text')}
              </div>
            </div>
            <div className={modalBoxStyles.stickyButton}>
              <Button
                fullWidth
                reverse
                as="a"
                href={`tel:${PHONES.INCORRECT_FINANCE_AMOUNT}`}
                icon="phone"
                label={`${formatPhoneNumber(PHONES.INCORRECT_FINANCE_AMOUNT).pretty}`}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default FinanceApplicable;
